import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { applyChannelMarkupAndRoundUpToTen, NO_CHANNEL_MARKUP } from 'lib/channelMarkup/channelMarkup'
import { getOfferChannelMarkupLeadPricing } from 'lib/channelMarkup/channelMarkupLeadPricing'
import { resolveChannelMarkupDetails } from 'lib/channelMarkup/channelMarkupV2'
import { useAppSelector } from 'hooks/reduxHooks'
import { getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { useMemo } from 'react'

function useBedbankOfferSell(
  offer: App.BedbankOffer | App.BedbankOfferSummary,
  duration: number | undefined,
  rooms: Array<App.Occupants> | undefined,
): App.BedbankSellPriceInfo | null {
  const markupSource = useAppSelector(getChannelMarkupSource)
  return useMemo(() => {
    if (offer.sell?.price) {
      const numberRooms = rooms?.length || 1
      const los = duration || offer.sell.los
      // Scale the un-marked base, then mark once. `offer.sell.price` already carries the markup for
      // `sell.los` nights and one room, so scaling that instead would multiply a `fixed` flat
      // amount by the nights and the rooms — `bedbankRateMap` charges it once for the stay.
      // `basePrice` falls back to `price` for offers held in redux from before it existed, where
      // no markup applied and the two were equal.
      const unmarkedBase = offer.sell.basePrice ?? offer.sell.price
      const basePrice = Math.round((unmarkedBase / offer.sell.los) * los * numberRooms)
      // `sell.channelMarkup` was decided on the lead for `sell.los` nights. A price band can fall the
      // other way for `los` nights, so decide again on the lead for the nights shown — the same lead
      // `bedbankRateMap` decides the stay's rates on, so this price matches the offer page and checkout.
      let channelMarkup = offer.sell.channelMarkup ?? NO_CHANNEL_MARKUP
      if (markupSource && los !== offer.sell.los) {
        channelMarkup = resolveChannelMarkupDetails(offer.id, offer.type, markupSource, getOfferChannelMarkupLeadPricing(offer, los))
      }
      const price = applyChannelMarkupAndRoundUpToTen(basePrice, channelMarkup)
      // `sell.value` stays un-marked so it can be scaled by nights and rooms first, the same way
      // `basePrice` is. Mark it after that scale, or a fixed amount would be multiplied by the stay.
      const unmarkedValue = Math.round((offer.sell.value / offer.sell.los) * los * numberRooms)
      const value = applyChannelMarkupAndRoundUpToTen(unmarkedValue, channelMarkup)
      const taxesAndFees = Math.round((offer.sell.taxesAndFees / offer.sell.los) * los * numberRooms)
      const propertyFees = Math.round((offer.sell.propertyFees / offer.sell.los) * los * numberRooms)
      const discountPercentage = getValueOffPercent(value, price + propertyFees)

      return {
        price,
        basePrice,
        channelMarkup: channelMarkup.isApplied ? channelMarkup : undefined,
        value,
        propertyFees,
        taxesAndFees,
        discountPercentage: discountPercentage >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? discountPercentage : 0,
        los,
      }
    }
    return null
  }, [offer, duration, rooms, markupSource])
}

export default useBedbankOfferSell
